import { useState } from "react";

import type { ResultImportSummaryResponse } from "@synthesis/shared-services/external/application/types";
import { useGetRequest as useGetInternalRequest } from "@synthesis/shared-services/internal/application/hooks";
import type { ImportSummaryResponse } from "@synthesis/shared-services/internal/application/types";
import { Button, Modal, NoticeBase, ScrollArea } from "@synthesis/shared-ui";
import { IconLoader } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";

import { useApplicationDictionariesContext } from "../../pages/application-detail-page/application-dictionaries-context";
import { useClientType } from "../../shared/hooks/use-client-type";
import { Uploader, type UploaderListItem } from "../uploader";
import { enrichImportResultWithRequestDetail, isImportItemFailed, mapImportItemToResultData } from "./import-application-result.lib";
import { ImportApplicationsResultView } from "./import-applications-result-view";
import { ImportApplicationResultView } from "./import-application-result-view";
import { ImportResultsResultView } from "./import-results-result-view";
import { ImportResultView } from "./import-result-view";
import { useImportApplicationMutation } from "./use-import-application-mutation";

export type ImportApplicationModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  requestId?: string;
  onImported?: () => void;
};

export const ImportApplicationModal = ({ isOpen, onOpenChange, requestId, onImported }: ImportApplicationModalProps) => {
  const clientType = useClientType();
  const isExternal = clientType === "external";
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isNoticeVisible, setIsNoticeVisible] = useState(true);
  const [showOnlyChanges, setShowOnlyChanges] = useState(false);

  const importMutation = useImportApplicationMutation({ requestId, onImported });

  const isSuccess = importMutation.isSuccess && importMutation.data != null;
  const internalImportSummary = !isExternal ? (importMutation.data as ImportSummaryResponse | undefined) : undefined;
  const externalImportSummary = isExternal ? (importMutation.data as ResultImportSummaryResponse | undefined) : undefined;
  const internalImportItems = internalImportSummary?.items ?? [];
  const externalImportItems = externalImportSummary?.items ?? [];
  const isMultiImportResult = (isExternal && externalImportItems.length > 1) || (!isExternal && internalImportItems.length > 1);

  // После успешного импорта подтягиваем заявку по id (страница деталей): поля «Тип заявки»,
  // «Инициатор», «Дорога», «Дата создания» в ответе импорта отсутствуют — их отдаёт GET /requests/{id}.
  // Переключатель «Отобразить только изменения» остаётся завязан на new/previous-поля из items.
  const { dictionaries } = useApplicationDictionariesContext();
  const internalDetailQuery = useGetInternalRequest(
    { path: { id: requestId ?? "" } },
    {
      query: {
        enabled: Boolean(requestId) && Boolean(!isExternal && internalImportItems.length === 1) && isSuccess,
      },
    },
  );

  const resetState = () => {
    setSelectedFile(null);
    setIsNoticeVisible(true);
    setShowOnlyChanges(false);
    importMutation.reset();
  };

  const handleOpenChange = (state: boolean) => {
    if (!state) {
      onOpenChange(false);
      resetState();
    }
  };

  const handleSelectFiles = (files: File[]) => {
    const file = files[0];
    setSelectedFile(file ?? null);
    importMutation.reset();
  };

  const handleDeleteFromList = (_fileMeta: UploaderListItem) => {
    setSelectedFile(null);
    importMutation.reset();
  };

  const handleImport = () => {
    if (selectedFile && !isSuccess) {
      importMutation.mutate({ body: { file: selectedFile } });
      return;
    }

    if (isSuccess) {
      handleOpenChange(false);
    }
  };

  const modalWidthClass = isSuccess && isMultiImportResult ? "w-[min(1100px,calc(100vw-2rem))]" : "w-[700px]";

  return (
    <Modal.Root isOpen={isOpen} setIsOpen={handleOpenChange}>
      <Modal.Content className={cn("flex max-h-[calc(100vh-4rem)] flex-col overflow-hidden", modalWidthClass)}>
        <Modal.Title>{isExternal ? "Импорт итогового результата" : "Импорт заявки"}</Modal.Title>
        <ScrollArea className="min-h-0 flex-1 overflow-x-hidden [&>[role='presentation']]:!min-w-full">
          <div className="p-8">
            {!isSuccess ? (
              <>
                <h3 className="heading-h4 after:text-system-error-50 mb-4 after:ml-2 after:content-['*']">
                  {isExternal ? "Архив итогового результата" : "Архив заявки"}
                </h3>
                {isNoticeVisible && (
                  <NoticeBase
                    className="mb-2"
                    titleAppearance="common"
                    title="Допустимый размер файла — 500Мб"
                    description="Файлы размером более 500Мб загрузить не получится."
                    status="info"
                    onClose={() => setIsNoticeVisible(false)}
                  />
                )}
                <div className="relative">
                  <Uploader
                    multiple={false}
                    isViewFileList
                    formats={["zip"]}
                    onSelectFiles={handleSelectFiles}
                    onDeleteFromList={handleDeleteFromList}
                    allowDeleteFromList
                  />
                  {importMutation.isPending && (
                    <div className="bg-neutrals-95 absolute inset-0 z-10 flex items-center justify-center">
                      <p className="paragraph-base text-type-minor flex items-center justify-center gap-2">
                        <IconLoader className="size-6 animate-spin" />
                        <span>Архив обрабатывается, подождите...</span>
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : isExternal && selectedFile && isMultiImportResult && externalImportSummary ? (
              <ImportResultsResultView fileName={selectedFile.name} summary={externalImportSummary} />
            ) : isExternal && selectedFile && externalImportItems[0] ? (
              <ImportResultView fileName={selectedFile.name} data={externalImportItems[0]} />
            ) : selectedFile && isMultiImportResult && internalImportSummary ? (
              <ImportApplicationsResultView fileName={selectedFile.name} summary={internalImportSummary} />
            ) : selectedFile && internalImportItems[0] ? (
              <ImportApplicationResultView
                fileName={selectedFile.name}
                data={enrichImportResultWithRequestDetail(mapImportItemToResultData(internalImportItems[0]), internalDetailQuery.data, dictionaries)}
                errors={internalImportItems[0].errors ?? undefined}
                isImportFailed={isImportItemFailed(internalImportItems[0])}
                showOnlyChanges={showOnlyChanges}
                onShowOnlyChangesChange={setShowOnlyChanges}
              />
            ) : selectedFile ? (
              <ImportApplicationsResultView fileName={selectedFile.name} summary={internalImportSummary ?? { items: [] }} />
            ) : null}
          </div>
        </ScrollArea>
        <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
          <Button
            type="button"
            theme={isSuccess ? "secondary" : "primary"}
            size="lg"
            disabled={!isSuccess && (!selectedFile || importMutation.isPending)}
            onClick={handleImport}
          >
            {isSuccess ? "Закрыть" : "Импортировать"}
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
